import { Badge } from "@/components/ui/badge/badge";
import { Card, CardContent } from "@/components/ui/card/card";
import { Skeleton } from "@/components/ui/skeleton/skeleton";
import { Spinner } from "@/components/ui/spinner/spinner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs/tabs";
import { usePersistedState } from "@/lib/usePersistedState";
import { EXDSOpLandingDocumentList } from "@/odata/appu4dm/AppU4DMModel";
import { useDocuments } from "@/queries/documents/use-documents";
import { useDowloadFile } from "@/queries/documents/use-download-file";
import { ChevronRight, DownloadIcon, EyeIcon } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ImageFile } from "../image-file/image-file";
import { cn } from "@/lib/utils";
import { QueryError } from "@/components/ui/query-error/query-error";

export interface DocumentsProps {
  selected?: string;
  condense?: boolean;
  onDocumentSelect: (document: EXDSOpLandingDocumentList) => void;
}

export function Documents({
  condense = false,
  selected,
  onDocumentSelect,
}: DocumentsProps) {
  const { documents, isPending, error, refetch } = useDocuments();
  const [selectedCategory, setSelectedCategory] = usePersistedState<
    string | undefined
  >("document_category", undefined);
  const { t } = useTranslation();

  const categories = {
    MaterialDefinitionDocumentOnMaterialDefinition: t("documents.tabs.product"),
    Machines: t("documents.tabs.machines"),
    Tools: t("documents.tabs.tools"),
    ToBeUsedDocument: t("documents.tabs.operation"),
    Material: t("documents.tabs.parts"),
    ActualCollectedDocuments: t("documents.tabs.runtime"),
  } as const;

  // This hook will take care of selecting the category
  // of the selected document
  useEffect(() => {
    const selectedDocument =
      selected &&
      // Try find the document in existing category. If it exists here, stay on this tab.
      // Else look for the document in other categories and change tab to this
      (documents?.find(
        (d) => d.Document === selected && d.Category === selectedCategory
      ) ||
        documents?.find((d) => d.Document === selected));
    if (!selectedDocument || !selectedDocument.Category) return;
    setSelectedCategory(selectedDocument.Category);
  }, [selected, documents]);

  if (isPending) return <DocumentsSkeleton />;

  if (error) {
    return (
      <QueryError
        resource={t("documents.title")}
        errors={[error]}
        retries={[refetch]}
      />
    );
  }

  if (!documents) return null;

  const grouped = documents.reduce<Record<string, EXDSOpLandingDocumentList[]>>(
    (result, item) => ({
      ...result,
      [item["Category"] ?? ""]: [
        ...(result[item["Category"] ?? ""] || []),
        item,
      ],
    }),
    {}
  );

  const categoriesWithDocuments = Object.keys(categories).filter(
    (category) => grouped[category]?.length
  );

  const selectedCategoryTab =
    selectedCategory &&
    categoriesWithDocuments.find((d) => d === selectedCategory)
      ? selectedCategory
      : Object.values(categoriesWithDocuments)[0];

  return (
    <Tabs
      value={selectedCategoryTab}
      className="flex flex-col h-full"
      onValueChange={setSelectedCategory}
    >
      <TabsList className="justify-start">
        {categoriesWithDocuments.map((category) => {
          return (
            <TabsTrigger
              key={category}
              value={category}
              className={cn("w-full", {
                "h-[56px]": condense,
              })}
            >
              <span className="inline-block truncate w-[99%]">
                {categories[category as keyof typeof categories]}
              </span>
              {!condense && (
                <Badge className="ml-2">{grouped[category].length}</Badge>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>
      <div
        className={cn("no-scrollbar overflow-auto", {
          "px-3 pb-2": condense,
        })}
      >
        {categoriesWithDocuments.map((category) => {
          return (
            <TabsContent key={category} value={category}>
              {grouped[category]?.map((doc) => (
                <DocumentCard
                  key={doc.Id}
                  active={doc.Document === selected}
                  condense={condense}
                  document={doc}
                  onDocumentSelect={onDocumentSelect}
                />
              ))}
            </TabsContent>
          );
        })}
      </div>
    </Tabs>
  );
}

const DocumentCard = function DocumentCard({
  active = false,
  condense = false,
  document,
  onDocumentSelect,
}: {
  active?: boolean;
  condense?: boolean;
  document: EXDSOpLandingDocumentList;
  onDocumentSelect: DocumentsProps["onDocumentSelect"];
}) {
  const { isPending, downloadFile } = useDowloadFile();
  const handleDocumentSelect = () => onDocumentSelect(document);
  const handleDocumentDownload = useCallback(async () => {
    if (document.FileId)
      downloadFile({
        id: document.FileId,
        fileName: document.FileName || "file",
      });
  }, [document.FileId, document.FileName, downloadFile]);

  const Icon = active ? EyeIcon : ChevronRight;
  const scrollIntoView = useCallback(
    (node: HTMLDivElement) => {
      if (!active || !node) return;
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    [active]
  );

  return (
    <div className="flex gap-2 justify-between py-1" ref={scrollIntoView}>
      <Card
        role="button"
        onClick={handleDocumentSelect}
        className={cn("flex-grow p-2 overflow-hidden", {
          "border-primary text-primary ring-2": active,
          "text-sm": condense,
        })}
      >
        <CardContent className="flex items-center gap-2 p-0 h-full min-h-[33px]">
          {document.IconId && (
            <ImageFile
              fileId={document.IconId}
              className="max-w-[60px] max-h-10"
            />
          )}
          <p className="text-ellipsis overflow-hidden whitespace-nowrap">
            {document.FileName}
          </p>
          <span className="ml-auto">
            <Icon className="h-5 w-5" />
          </span>
        </CardContent>
      </Card>
      {!condense && (
        <div
          role="button"
          onClick={handleDocumentDownload}
          data-testid="document-download-button"
          className="flex h-auto justify-center items-center border border-primary rounded-sm text-primary py-2 px-3"
        >
          {!isPending && <DownloadIcon />}
          {isPending && <Spinner className="h-6 w-6 mr-0" />}
        </div>
      )}
    </div>
  );
};

function DocumentsSkeleton() {
  return (
    <div className="flex flex-col gap-5 p-4">
      <div className="flex gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="w-[120px] h-10" />
        ))}
      </div>
      <div className="flex flex-col gap-2">
        {Array.from({ length: 24 }).map((_, i) => (
          <div key={i} className="flex gap-2 h-12">
            <Skeleton className="w-[90%]" />
            <Skeleton className="w-[10%]" />
          </div>
        ))}
      </div>
    </div>
  );
}
